import {styled} from "@mui/material/styles";
import {memo, useCallback, useEffect, useRef, useState} from "react";

const scrollbarWidthPx = 16;
const scrollbarTopPx = 56;
const scrollbarBottomInsetPx = 16;
const minScrollbarHeightPx = 56;

const Anchor = styled("div")({
    height: 0,
});

const NativeScrollbar = styled("div", {
    shouldForwardProp: (prop) => prop !== "leftPx" && prop !== "topPx",
})<{leftPx: number; topPx: number}>(({leftPx, topPx}) => ({
    position: "fixed",
    top: topPx,
    left: leftPx,
    width: scrollbarWidthPx,
    height: `calc(100vh - ${topPx + scrollbarBottomInsetPx}px)`,
    overflowX: "hidden",
    overflowY: "scroll",
    overscrollBehavior: "contain",
    zIndex: 10,
}));

const ScrollSpace = styled("div", {shouldForwardProp: (prop) => prop !== "heightPx"})<{heightPx: number}>(
    ({heightPx}) => ({
        width: 1,
        height: heightPx,
    }),
);

/**
 * Hides the native window scrollbar while enabled. Wheel, touch, keyboard,
 * and programmatic scrolling are unaffected. The list-aligned overflow
 * element renders the browser scrollbar instead.
 */
export function useHideNativeScrollbar(enabled: boolean): void {
    useEffect(() => {
        if (!enabled) return undefined;
        const style = document.createElement("style");
        style.textContent = "html{scrollbar-width:none;} html::-webkit-scrollbar{display:none;}";
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [enabled]);
}

/**
 * Native browser scrollbar that mirrors the window scroll range.
 */
function SearchListingScrollbar() {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const scrollbarRef = useRef<HTMLDivElement | null>(null);
    const mirroredScrollTopRef = useRef<number>();
    const [leftPx, setLeftPx] = useState<number>();
    const [topPx, setTopPx] = useState(scrollbarTopPx);
    const [scrollSpaceHeightPx, setScrollSpaceHeightPx] = useState(0);
    const [isScrollable, setIsScrollable] = useState(false);

    const syncFromWindow = useCallback(() => {
        const scrollbar = scrollbarRef.current;
        if (!scrollbar) return;
        const maxWindowScrollPx = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const maxScrollbarScrollPx = Math.max(0, scrollbar.scrollHeight - scrollbar.clientHeight);
        const targetScrollTop = maxWindowScrollPx > 0 ? (window.scrollY / maxWindowScrollPx) * maxScrollbarScrollPx : 0;
        if (Math.abs(scrollbar.scrollTop - targetScrollTop) > 1) {
            mirroredScrollTopRef.current = targetScrollTop;
            scrollbar.scrollTop = targetScrollTop;
        }
    }, []);

    const remeasure = useCallback(() => {
        const anchor = anchorRef.current;
        const scrollbar = scrollbarRef.current;
        if (anchor) {
            const rect = anchor.getBoundingClientRect();
            const left = Math.min(rect.right + 18, window.innerWidth) - scrollbarWidthPx;
            setLeftPx((previous) => (previous === left ? previous : left));
            const top = Math.min(
                Math.max(scrollbarTopPx, Math.round(rect.top)),
                window.innerHeight - scrollbarBottomInsetPx - minScrollbarHeightPx,
            );
            setTopPx((previous) => (previous === top ? previous : top));
        }

        const documentHeightPx = document.documentElement.scrollHeight;
        const maxWindowScrollPx = Math.max(0, documentHeightPx - window.innerHeight);
        setIsScrollable(maxWindowScrollPx > 1);
        if (scrollbar && window.innerHeight > 0) {
            const height = scrollbar.clientHeight * (documentHeightPx / window.innerHeight);
            setScrollSpaceHeightPx((previous) => (previous === height ? previous : height));
        }
        syncFromWindow();
    }, [syncFromWindow]);

    useEffect(() => {
        let frame: number | undefined;
        const scheduleRemeasure = () => {
            if (frame !== undefined) return;
            frame = requestAnimationFrame(() => {
                frame = undefined;
                remeasure();
            });
        };
        remeasure();
        window.addEventListener("scroll", scheduleRemeasure, {passive: true});
        window.addEventListener("resize", scheduleRemeasure);
        const resizeObserver = new ResizeObserver(scheduleRemeasure);
        resizeObserver.observe(document.body);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("scroll", scheduleRemeasure);
            window.removeEventListener("resize", scheduleRemeasure);
            if (frame !== undefined) cancelAnimationFrame(frame);
        };
    }, [remeasure]);

    useEffect(() => {
        if (isScrollable) remeasure();
    }, [isScrollable, remeasure]);

    useEffect(() => {
        syncFromWindow();
    }, [scrollSpaceHeightPx, syncFromWindow]);

    const onScroll = useCallback(() => {
        const scrollbar = scrollbarRef.current;
        if (!scrollbar) return;
        const mirroredScrollTop = mirroredScrollTopRef.current;
        if (mirroredScrollTop !== undefined && Math.abs(scrollbar.scrollTop - mirroredScrollTop) <= 1) {
            return;
        }
        mirroredScrollTopRef.current = undefined;
        const maxScrollbarScrollPx = Math.max(0, scrollbar.scrollHeight - scrollbar.clientHeight);
        const maxWindowScrollPx = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const targetWindowScrollPx =
            maxScrollbarScrollPx > 0 ? (scrollbar.scrollTop / maxScrollbarScrollPx) * maxWindowScrollPx : 0;
        if (Math.abs(window.scrollY - targetWindowScrollPx) > 1) {
            window.scrollTo({top: targetWindowScrollPx});
        }
    }, []);

    const onUserScrollStart = useCallback(() => {
        mirroredScrollTopRef.current = undefined;
    }, []);

    return (
        <>
            <Anchor ref={anchorRef} aria-hidden={true} />
            {leftPx !== undefined && isScrollable && (
                <NativeScrollbar
                    ref={scrollbarRef}
                    leftPx={leftPx}
                    topPx={topPx}
                    onScroll={onScroll}
                    onPointerDown={onUserScrollStart}
                    onWheel={onUserScrollStart}
                    onKeyDown={onUserScrollStart}
                    aria-hidden={true}
                    data-testid="search-listing-scrollbar"
                >
                    <ScrollSpace heightPx={scrollSpaceHeightPx} />
                </NativeScrollbar>
            )}
        </>
    );
}

export default memo(SearchListingScrollbar);
